import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Lenis from "lenis";
import { Phone, ArrowLeft, CheckCircle2 } from "lucide-react";
import SEO from "./SEO";
import Nav from "./Nav";
import Contact from "./Contact";
import StickyCTA from "./StickyCTA";
import QuoteDialog from "./QuoteDialog";
import { useContent } from "../ContentContext";

/**
 * Reusable local-SEO service landing page shell.
 *
 * Every service page renders identical branding (Nav, TopBar, Contact section,
 * QuoteDialog, StickyCTA) so the visual identity stays the same as the
 * homepage — only the hero content and the middle body copy change.
 *
 * IMPORTANT: props.body should contain FACT-BASED copy only. Do NOT include
 * claims (certifications, response times, licenses) that are not already
 * confirmed on the homepage / business info.
 */
export default function ServicePage({
  slug,
  title,        // <title>
  description,  // <meta description>
  h1,           // on-page hero heading
  intro,        // hero paragraph (~2 sentences)
  bullets,      // array of "what's included" bullets
  body,         // array of {heading, text} sections
  relatedLinks, // array of {to, label}
  serviceName,  // schema.org Service name
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("estimate");
  const openDialog = (m) => { setMode(m); setOpen(true); };
  const { company } = useContent();

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": serviceName,
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://topofthelinetreeservicesllc.com/#business",
      "name": "Top of the Line Tree Services LLC",
    },
    "areaServed": [
      { "@type": "City", "name": "Huntsville", "sameAs": "https://en.wikipedia.org/wiki/Huntsville,_Alabama" },
      { "@type": "City", "name": "Madison" },
      { "@type": "City", "name": "Decatur" },
      { "@type": "City", "name": "Athens" },
      { "@type": "City", "name": "Harvest" },
      { "@type": "City", "name": "Meridianville" },
      { "@type": "City", "name": "Hazel Green" },
      { "@type": "State", "name": "Alabama" },
    ],
    "url": `https://topofthelinetreeservicesllc.com${slug}`,
    "description": description,
  };

  return (
    <div style={{ background: "var(--bone)" }} data-testid={`service-page-${slug.replace(/\//g, "")}`}>
      <SEO title={title} description={description} path={slug} jsonLd={serviceJsonLd} />

      <div className="w-full text-center py-2 text-xs font-medium tracking-wide"
           style={{ background: "var(--canopy)", color: "var(--moss)" }}>
        {company?.emergency_247 ? "24/7 Emergency Tree Service · " : ""}
        <a href={`tel:${company?.phoneRaw || "+12563758064"}`}
           className="inline-flex items-center gap-1 font-semibold"
           style={{ color: "var(--brass)" }}
           data-testid="service-topbar-phone">
          <Phone size={12} /> {company?.phone || "(256) 375-8064"}
        </a>
      </div>

      <Nav onEstimate={() => openDialog("estimate")} />

      {/* HERO — reuses site typography and colors, no new visual style */}
      <section className="relative overflow-hidden" style={{ background: "var(--pine)" }}>
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-32 relative z-10">
          <Link to="/"
                className="inline-flex items-center gap-2 mb-6 text-sm font-semibold"
                style={{ color: "var(--brass)" }}
                data-testid="service-back-home">
            <ArrowLeft size={14} /> Back to home
          </Link>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
            style={{ color: "var(--moss)" }}>
            {h1}
          </motion.h1>
          <p className="mt-6 max-w-3xl text-lg leading-relaxed"
             style={{ color: "rgba(240,235,225,0.85)" }}>
            {intro}
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <button
              onClick={() => openDialog("estimate")}
              data-testid="service-cta-estimate"
              className="px-6 py-3 rounded-full font-bold uppercase text-sm tracking-wide"
              style={{ background: "var(--brass)", color: "var(--pine)" }}>
              Request a free estimate
            </button>
            <a href={`tel:${company?.phoneRaw || "+12563758064"}`}
               data-testid="service-cta-call"
               className="px-6 py-3 rounded-full font-bold uppercase text-sm tracking-wide border-2"
               style={{ borderColor: "var(--brass)", color: "var(--brass)" }}>
              Call {company?.phone || "(256) 375-8064"}
            </a>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      {bullets && bullets.length > 0 && (
        <section className="py-16 md:py-24" style={{ background: "var(--bone)" }}>
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl mb-8"
                style={{ color: "var(--pine)" }}>
              What&rsquo;s included
            </h2>
            <ul className="grid sm:grid-cols-2 gap-4">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="mt-1 flex-shrink-0" style={{ color: "var(--brass)" }} />
                  <span className="text-base leading-relaxed" style={{ color: "var(--pine)" }}>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* BODY SECTIONS */}
      {body && body.length > 0 && (
        <section className="py-16 md:py-24" style={{ background: "rgba(29,62,46,0.06)" }}>
          <div className="max-w-3xl mx-auto px-6 md:px-12 space-y-10">
            {body.map((sec, i) => (
              <div key={i}>
                <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl mb-4"
                    style={{ color: "var(--pine)" }}>{sec.heading}</h2>
                <div className="prose max-w-none text-base leading-relaxed" style={{ color: "rgba(29,62,46,0.85)" }}>
                  {sec.text.split("\n\n").map((para, j) => <p key={j} className="mb-4">{para}</p>)}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* RELATED SERVICES */}
      {relatedLinks && relatedLinks.length > 0 && (
        <section className="py-16" style={{ background: "var(--bone)" }}>
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl mb-6"
                style={{ color: "var(--pine)" }}>Related services</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {relatedLinks.map((l, i) => (
                <Link key={i} to={l.to}
                      data-testid={`service-related-${i}`}
                      className="block p-5 rounded-2xl border transition-all hover:-translate-y-1"
                      style={{ borderColor: "rgba(29,62,46,0.15)", background: "white", color: "var(--pine)" }}>
                  <div className="font-bold uppercase text-sm tracking-wide">{l.label}</div>
                  <div className="mt-1 text-xs" style={{ color: "var(--brass)" }}>Learn more →</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Contact onEstimate={() => openDialog("estimate")} />
      <StickyCTA onEstimate={() => openDialog("estimate")} />
      <QuoteDialog open={open} onOpenChange={setOpen} mode={mode} />
    </div>
  );
}
